import { useEffect, useRef, useState, type ReactNode } from 'react';
import { TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { fieldSx } from './formStyles';
import { searchPlaces, suggestionVenue, type PlaceSuggestion } from '../schedule/placeSearch';

/**
 * A location input with address suggestions underneath.
 *
 * Free text always works; picking a suggestion also supplies coordinates, which
 * is what gives an event a map pin and travel times. Shared by the personal
 * event form and the edit form.
 */

const Suggestions = styled('ul')({
  listStyle: 'none',
  margin: '4px 0 0',
  padding: 4,
  maxHeight: 190,
  overflowY: 'auto',
  borderRadius: 8,
  background: 'rgba(0,0,0,0.28)',
  border: '1px solid rgba(243,238,231,0.18)',
});

const Suggestion = styled('button')({
  appearance: 'none',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: '7px 9px',
  cursor: 'pointer',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  '&:hover, &:focus-visible': { background: 'rgba(243,238,231,0.12)' },
});

interface Props {
  value: string;
  coords: [number, number] | null;
  onChange: (venue: string, coords: [number, number] | null) => void;
  label?: string;
  placeholder?: string;
  /** Shown under the field; told whether the current value is pinned. */
  hint?: (located: boolean) => ReactNode;
}

export default function PlaceField({
  value,
  coords,
  onChange,
  label = 'Location',
  placeholder = 'Start typing an address',
  hint,
}: Props) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  /** Suppresses the lookup for the change a picked suggestion causes. */
  const skipNext = useRef(false);
  /** Only look up what the user typed, not the value the field opened with. */
  const typed = useRef(false);

  /**
   * Debounced, and each in-flight request is abandoned when the next starts, so
   * a slow answer to "5 Mad" can't replace a fast one to "5 Madison Ave".
   */
  useEffect(() => {
    if (skipNext.current || !typed.current) {
      skipNext.current = false;
      return;
    }
    if (value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      void searchPlaces(value, controller.signal).then(setSuggestions);
    }, 220);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  return (
    <div>
      <TextField
        label={label}
        fullWidth
        value={value}
        onChange={(e) => {
          typed.current = true;
          onChange(e.target.value, null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && suggestions.length) {
            e.stopPropagation();
            setSuggestions([]);
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        sx={fieldSx}
      />
      {suggestions.length > 0 && (
        <Suggestions>
          {suggestions.map((s) => (
            <li key={`${s.label}|${s.detail}`}>
              <Suggestion
                type="button"
                onClick={() => {
                  skipNext.current = true;
                  setSuggestions([]);
                  onChange(suggestionVenue(s), s.coords);
                }}
              >
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{s.label}</Typography>
                {s.detail && <Typography sx={{ fontSize: 11.5, opacity: 0.68 }}>{s.detail}</Typography>}
              </Suggestion>
            </li>
          ))}
        </Suggestions>
      )}
      {hint && (
        <Typography component="div" sx={{ fontSize: 11, opacity: 0.6, mt: 0.6, lineHeight: 1.45 }}>
          {hint(Boolean(coords))}
        </Typography>
      )}
    </div>
  );
}
